"use client";

import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useMotionValueEvent, useScroll } from "framer-motion";
import { Button, ButtonLink } from "@/components/ui/button";
import Search from "@/components/icons/Search";
import ChevronFill from "@/components/icons/ChevronFill";
import { cn, strapiMediaAlt, strapiMediaUrl } from "@/lib/utils";
import MobileMenu from "@/components/general/MobileMenu";
import { Hamburger } from "@/components/ui/hamburger";
import { usePathname, useRouter } from "next/navigation";
import useWindowSize from "@/hooks/useWindowSize";
import { useStore } from "@/store";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import ResponsiveImage from "@/components/ui/ResponsiveImage";
import ProvinceMenuSection from "@/components/general/ProvinceMenuSection";

const HeaderNSC = ({ menuData, tagsData, user, provinces }: any) => {
  const pathname = usePathname();
  const router = useRouter();
  const [openSearchDialog, setOpenSearchDialog] = useState(false);
  const [search, setSearch] = useState("");
  const [transparent, setTransparent] = useState(false);
  const [hover, setHover] = useState(false);
  const [hideTopHeader, setHideTopHeader] = useState(false);
  const [openMegaMenu, setOpenMegaMenu] = useState(false);
  const [open, setOpen] = useState(false);
  const [megaOffset, setMegaOffset] = useState(0);
  const menuRef = useRef<HTMLUListElement>(null);
  const { width } = useWindowSize();
  const { setTagsData, setProvinces } = useStore();
  const [hideTopBar, setHideTopBar] = useState(false);
  const { scrollY } = useScroll();

  useEffect(() => {
    setTagsData(tagsData);
  }, [tagsData, setTagsData]);

  useEffect(() => {
    setProvinces(provinces);
  }, [provinces, setProvinces]);

  useEffect(() => {
    setOpen(false);
    setOpenMegaMenu(false);
  }, [pathname]);

  useEffect(() => {
    if (!menuRef.current) return;
    setMegaOffset(menuRef.current.getBoundingClientRect().left);

    const handleResize = () => {
      if (!menuRef.current) return;
      setMegaOffset(menuRef.current.getBoundingClientRect().left);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Handle scroll direction for mobile top bar hiding
  useMotionValueEvent(scrollY, "change", (latest) => {
    // Simple threshold-based hiding: hide when scrolled down past 100px, show when back near top
    if (width < 768) {
      if (latest > 100) {
        console.log("Setting hideTopBar to true");
        setHideTopBar(true);
      } else if (latest < 50) {
        console.log("Setting hideTopBar to false");
        setHideTopBar(false);
      }
    } else {
      // Always show on desktop
      setHideTopBar(false);
    }
  });

  const currentUrl = pathname === "/" ? "/home" : pathname;

  return (
    <motion.header
      onMouseEnter={() => width > 768 && setHover(true)}
      onMouseLeave={() => {
        width > 768 && setHover(false);
        setOpenMegaMenu(false);
      }}
      initial={false}
      animate={{
        y: hideTopHeader ? (menuData?.topBar?.enabled ? -80 : -40) : 0,
        backgroundColor: hideTopHeader || openMegaMenu || open || !transparent || hover ? "#171c60" : "#171c6000",
      }}
      transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1],
        backgroundColor: { duration: !transparent ? 0 : 0.5, ease: [0.16, 1, 0.3, 1] },
      }}
      className={cn("fixed left-0 top-0 z-[200] h-auto w-full", "bg-bg-transparent")}
    >
      {/* Top Bar */}
      <AnimatePresence>
        {menuData?.topBar?.enabled && (!hideTopBar || width >= 768) && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{
              duration: 0.3,
              ease: [0.16, 1, 0.3, 1],
            }}
          >
            {menuData?.topBar?.Url ? (
              <Link
                href={menuData?.topBar?.Url}
                style={{ background: menuData.topBar.backgroundColor }}
                className="min-h-[40px] w-full bg-[rgba(255,255,255,0.2)] block"
              >
                <div className="container h-full">
                  <div className="mx-auto flex h-full items-center justify-center gap-6 py-2">
                    <p className="font-oswald text-sm md:text-base uppercase text-white text-center">{menuData.topBar.text}</p>
                  </div>
                </div>
              </Link>
            ) : (
              <div
                style={{ background: menuData.topBar.backgroundColor }}
                className="min-h-[40px] w-full bg-[rgba(255,255,255,0.2)] block"
              >
                <div className="container h-full">
                  <div className="mx-auto flex h-full items-center justify-center gap-6 py-2">
                    <p className="font-oswald text-sm md:text-base uppercase text-white text-center">{menuData.topBar.text}</p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top header */}
      <div className={cn("hidden h-[40px] w-full md:block", "bg-[rgba(255,255,255,0.2)]")}>
        <div className="container">
          <div className="ml-auto flex w-fit items-center gap-6">
            <Dialog open={openSearchDialog} onOpenChange={setOpenSearchDialog}>
              <DialogTrigger asChild>
                <Button variant="ghost" className="px-0 text-white">
                  <Search color="white" className="mr-3" /> Zoeken
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader className="mb-6 flex flex-col gap-2">
                  <DialogTitle>Zoeken</DialogTitle>
                  <DialogDescription>Vul hieronder je zoekopdracht in</DialogDescription>
                </DialogHeader>

                <form
                  onSubmit={(e: any) => {
                    e.preventDefault();
                    if (search === "") return;
                    router.push(`
                        /zoeken?filter=${search === "" ? "all" : search}
                      `);
                    setOpenSearchDialog(false);
                  }}
                  className="flex items-center"
                >
                  <div className="grid flex-1 gap-2">
                    <Label htmlFor="link" className="sr-only">
                      Zoekopdracht
                    </Label>
                    <Input
                      onChange={(e: any) => {
                        setSearch(e.target.value);
                      }}
                      id="search"
                      type="text"
                      placeholder="Zoekopdracht"
                    />
                  </div>
                  <Button type="submit" size="lg" variant="secondary">
                    <Search color="white" className="mr-0" />
                    <span className="sr-only">Zoeken</span>
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* <ButtonLink variant="ghost" className="text-white" href="/contact">
              Contact
            </ButtonLink> */}
            {user ? (
              <ButtonLink variant="ghost" className="text-white" href="/mijn-account">
                {user.username}
              </ButtonLink>
            ) : (
              <ButtonLink variant="ghost" className="text-white" href="/mijn-account/inloggen">
                Inloggen
              </ButtonLink>
            )}
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="container my-4 grid h-11 grid-cols-[auto,1fr,auto] items-center justify-between md:my-[17px] lg:gap-x-2">
        <Link aria-label="" href="/">
          <span className="sr-only">Home</span>
          <div className={cn("relative", "h-[36px] w-[80px] md:h-11 md:w-24")} color="white">
            <ResponsiveImage
              src={strapiMediaUrl(menuData?.logo)}
              alt={strapiMediaAlt(menuData?.logo)}
              fill
              priority
              sizes="(max-width: 600px) 100vw, 50vw"
              className={cn("object-contain")}
            />
          </div>
        </Link>
        <nav className="hidden justify-center lg:flex">
          <motion.ul layout layoutRoot ref={menuRef} className={cn("flex list-none gap-8 xl:gap-10", "text-white")}>
            {menuData?.menu?.items.map((item: any, index: number) => (
              <MenuItem
                item={item}
                setOpenMegaMenu={setOpenMegaMenu}
                megaOffset={megaOffset}
                openMegaMenu={openMegaMenu}
                pathname={currentUrl}
                key={`${item.url}-${index}`}
              />
            ))}
          </motion.ul>
        </nav>
        <div className="hidden lg:flex">
          <ButtonLink variant="default" size="default" href="/lid-worden">
            Lid worden
          </ButtonLink>
        </div>
        <Hamburger open={open} setOpen={setOpen} />
      </div>

      {/* Mobile menu */}
      <MobileMenu open={open} menuData={menuData} />
    </motion.header>
  );
};

const MenuItem = ({ item, setOpenMegaMenu, megaOffset, openMegaMenu, pathname }: any) => {
  const [hover, setHover] = useState(false);

  return item.type === "wrapper_element" ? (
    <NestedItem
      megaOffset={megaOffset}
      openMegaMenu={openMegaMenu}
      setOpenMegaMenu={setOpenMegaMenu}
      key={item.url}
      item={item}
    />
  ) : (
    <li
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      key={item.url}
      className="leading-0 relative font-oswald text-base font-bold uppercase"
    >
      <Link target={item.target} href={item.url}>
        {item.title}
      </Link>
    </li>
  );
};

const NestedItem = ({
  item,
  setOpenMegaMenu,
  megaOffset,
  openMegaMenu,
}: {
  item: any;
  setOpenMegaMenu: React.Dispatch<React.SetStateAction<boolean>>;
  megaOffset: number;
  openMegaMenu: boolean;
}) => {
  const [hover, setHover] = useState(false);

  return (
    <li
      onMouseOver={() => {
        setOpenMegaMenu(item.id);
        setHover(true);
      }}
      onMouseLeave={() => {
        setHover(false);
      }}
      key={item.url}
      className="leading-0 font-oswald text-base font-bold uppercase"
    >
      <button className="relative flex items-center uppercase">
        {item.title}{" "}
        <ChevronFill
          className={cn("ml-2 transition-transform", openMegaMenu === item.id ? "!rotate-0" : "!rotate-180")}
          color="white"
          direction="down"
        />
      </button>
      <MegaMenu tag={item.tag} megaOffset={megaOffset} open={openMegaMenu === item.id} items={item.nestedItems} />
    </li>
  );
};

const MegaMenu = ({ items, open, megaOffset, tag }: { items: any; open: boolean; megaOffset: number; tag: string }) => {
  const { provinces } = useStore();
  const [provinceSubMenuOpen, setProvinceSubMenuOpen] = useState(false);

  // Check if province submenus are open to determine min height
  const needsExtraHeight = provinceSubMenuOpen;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      animate={{ opacity: open ? 1 : 0 }}
      style={{ pointerEvents: open ? "all" : "none" }}
      className={cn(
        "absolute left-0 -z-20 mt-[27px] h-auto w-screen pb-[52px] pt-[37px]", 
        "bg-nsc-dark-blue",
        needsExtraHeight ? "min-h-[550px]" : "min-h-auto"
      )}
    >
      <div
        style={{ transform: `translateX(${megaOffset}px)` }}
        className="grid grid-cols-[auto,1fr,auto] gap-x-2 lg:gap-x-12"
      >
        <ul className={cn("flex list-none flex-col gap-4", "text-white")}>
          {items &&
            items.length > 0 &&
            items.map((item: any, index: number) => {
              // Special handling for Provincies menu item
              if (item.type === "province_wrapper") {
                return (
                  <ProvinceMenuSection 
                    key={`provincies-${index}`} 
                    item={item} 
                    provinces={provinces}
                    onProvinceSubMenuChange={setProvinceSubMenuOpen}
                  />
                );
              }

              return (
                <MenuItem
                  item={{
                    ...item,
                    tag,
                  }}
                  megaOffset={megaOffset}
                  key={`${item.url}-${index}`}
                />
              );
            })}
        </ul>
      </div>
    </motion.div>
  );
};

export default HeaderNSC;
